// src/migration/engine.ts
function stepKey(family, fromVersion, toVersion) {
  return `${family}:${fromVersion}->${toVersion}`;
}
function deepClone(data) {
  return JSON.parse(JSON.stringify(data));
}
var MigrationRegistry = class {
  /**
   * Primary step storage: stepKey(family, from, to) → MigrationStep
   */
  steps = /* @__PURE__ */ new Map();
  /**
   * Family index: family → sorted array of registered fromVersions.
   * Each element `from` implies a step from `from` to `from + 1` (since steps
   * must be sequential integers per the contract). We store only fromVersion
   * because toVersion is always fromVersion + 1 for contiguous chains, but
   * we validate the actual toVersion stored in the MigrationStep during lookup.
   */
  familyIndex = /* @__PURE__ */ new Map();
  // -------------------------------------------------------------------------
  // Registration
  // -------------------------------------------------------------------------
  /**
   * Register a single migration step.
   *
   * Silently overwrites an existing step for the same (family, from, to) tuple
   * so that test re-use and hot reload work without requiring `clear()` first.
   */
  register(step) {
    if (step.fromVersion >= step.toVersion) {
      throw new Error(
        `MigrationRegistry.register: fromVersion (${step.fromVersion}) must be less than toVersion (${step.toVersion}) for family "${step.family}"`
      );
    }
    const key = stepKey(step.family, step.fromVersion, step.toVersion);
    this.steps.set(key, step);
    if (!this.familyIndex.has(step.family)) {
      this.familyIndex.set(step.family, /* @__PURE__ */ new Set());
    }
    this.familyIndex.get(step.family).add(step.fromVersion);
  }
  /**
   * Register multiple steps that must form a contiguous ascending chain.
   *
   * Contiguous means: step[i].toVersion === step[i+1].fromVersion for all i.
   * Also validates that every individual step has fromVersion < toVersion.
   *
   * @throws if the steps array is empty, any step is individually invalid, or
   *         the chain has gaps or is out of order.
   */
  registerChain(family, steps) {
    if (steps.length === 0) {
      throw new Error(
        `MigrationRegistry.registerChain: steps array must not be empty for family "${family}"`
      );
    }
    for (const step of steps) {
      if (step.family !== family) {
        throw new Error(
          `MigrationRegistry.registerChain: step family "${step.family}" does not match chain family "${family}"`
        );
      }
      if (step.fromVersion >= step.toVersion) {
        throw new Error(
          `MigrationRegistry.registerChain: step ${step.fromVersion}->${step.toVersion} in family "${family}" has fromVersion >= toVersion`
        );
      }
    }
    const sorted = [...steps].sort((a, b) => a.fromVersion - b.fromVersion);
    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];
      if (current.toVersion !== next.fromVersion) {
        throw new Error(
          `MigrationRegistry.registerChain: chain gap in family "${family}" \u2014 step ${current.fromVersion}->${current.toVersion} is not contiguous with ${next.fromVersion}->${next.toVersion}`
        );
      }
    }
    for (const step of sorted) {
      this.register(step);
    }
  }
  // -------------------------------------------------------------------------
  // Path finding
  // -------------------------------------------------------------------------
  /**
   * Return the ordered list of steps needed to migrate from `fromVersion` to
   * `toVersion`, or `null` if no complete path exists.
   *
   * Path finding is a forward walk: v1→v2→v3→…→vN.  The registry only
   * contains unit-increment steps (fromVersion + 1 === toVersion is the normal
   * contract, but multi-version jumps are also supported if explicitly
   * registered).  The walk greedily tries fromVersion+1 at each node, which
   * is sufficient for sequential integer versioning.
   */
  getMigrationPath(family, fromVersion, toVersion) {
    if (fromVersion >= toVersion) {
      return null;
    }
    const path = [];
    let current = fromVersion;
    while (current < toVersion) {
      const nextVersion = current + 1;
      const unitKey = stepKey(family, current, nextVersion);
      if (this.steps.has(unitKey)) {
        path.push(this.steps.get(unitKey));
        current = nextVersion;
        continue;
      }
      const fromSet = this.familyIndex.get(family);
      if (!fromSet || !fromSet.has(current)) {
        return null;
      }
      let bestStep = null;
      for (const [key, step] of this.steps.entries()) {
        if (key.startsWith(`${family}:${current}->`) && step.toVersion > current && step.toVersion <= toVersion) {
          if (bestStep === null || step.toVersion > bestStep.toVersion) {
            bestStep = step;
          }
        }
      }
      if (bestStep === null) {
        return null;
      }
      path.push(bestStep);
      current = bestStep.toVersion;
    }
    return path.length > 0 ? path : null;
  }
  /**
   * Return true iff a complete migration path exists from `fromVersion` to
   * `toVersion` for the given family.
   */
  canMigrate(family, fromVersion, toVersion) {
    return this.getMigrationPath(family, fromVersion, toVersion) !== null;
  }
  // -------------------------------------------------------------------------
  // Introspection
  // -------------------------------------------------------------------------
  /** Return all families that have at least one registered step. */
  getRegisteredFamilies() {
    return Array.from(this.familyIndex.keys()).sort();
  }
  /**
   * Return the minimum `fromVersion` and maximum `toVersion` across all steps
   * registered for the given family, or `null` if the family is unknown.
   */
  getVersionRange(family) {
    const fromSet = this.familyIndex.get(family);
    if (!fromSet || fromSet.size === 0) {
      return null;
    }
    let min = Infinity;
    let max = -Infinity;
    for (const [key, step] of this.steps.entries()) {
      if (key.startsWith(`${family}:`)) {
        if (step.fromVersion < min) min = step.fromVersion;
        if (step.toVersion > max) max = step.toVersion;
      }
    }
    return min === Infinity ? null : { min, max };
  }
  /** Remove all registered steps and family index entries. */
  clear() {
    this.steps.clear();
    this.familyIndex.clear();
  }
};
function migrate(data, family, fromVersion, toVersion, registry) {
  if (fromVersion >= toVersion) {
    return {
      success: false,
      error: `fromVersion (${fromVersion}) must be less than toVersion (${toVersion})`,
      failedAtVersion: fromVersion
    };
  }
  const path = registry.getMigrationPath(family, fromVersion, toVersion);
  if (path === null) {
    return {
      success: false,
      error: `No migration path found for family "${family}" from version ${fromVersion} to ${toVersion}`,
      failedAtVersion: fromVersion
    };
  }
  let current = deepClone(data);
  for (const step of path) {
    try {
      const transformed = step.migrate(current);
      if (transformed === null || typeof transformed !== "object" || Array.isArray(transformed)) {
        return {
          success: false,
          error: `Migration step ${step.fromVersion}->${step.toVersion} for family "${family}" returned a non-object value`,
          failedAtVersion: step.fromVersion
        };
      }
      current = transformed;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return {
        success: false,
        error: `Migration step ${step.fromVersion}->${step.toVersion} for family "${family}" threw: ${message}`,
        failedAtVersion: step.fromVersion
      };
    }
  }
  return {
    success: true,
    data: current,
    migratedFrom: fromVersion,
    migratedTo: toVersion,
    stepsApplied: path.length
  };
}
function createMigrationStep(family, from, to, fn) {
  if (from >= to) {
    throw new Error(
      `createMigrationStep: fromVersion (${from}) must be less than toVersion (${to}) for family "${family}"`
    );
  }
  return {
    family,
    fromVersion: from,
    toVersion: to,
    migrate: fn
  };
}

export {
  MigrationRegistry,
  migrate,
  createMigrationStep
};
//# sourceMappingURL=chunk-GWQWEMUY.js.map